import React, { useState, useEffect, memo, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Plus, Upload, X } from 'lucide-react';
import { toast } from 'react-toastify';
import Breadcrumb from '../components/breadcrumb';
import StepProgressBar from '../components/StepProgressBar';
import MultiContractorTradesStep from '../components/jobsite-visits/MultiContractorTradesStep';
import Dialog from '../components/Dialog';
import SelectDropdown from '../components/select';
import { JobsiteDetailsSkeleton } from '../components/Skeleton';
import { JobsiteVisitsService } from '../services/jobsite-visits.service';
import { JobsiteService } from '../services/jobsite.service';
import { ContractorService } from '../services/contractor.service';
import { authService } from '../services/auth.service';
import { useJobsiteData } from '../hooks/useJobsiteData';
import { useEditForm } from '../hooks/useEditForm';
import { useJobsiteVisitFileUpload } from '../hooks/useJobsiteVisitFileUpload';
import {
  JobsiteVisitDTO,
  CreateJobsiteVisitsTradeDTO,
  JobsiteVisitFilesDTO,
  JobTradeVisitDTO,
  ContractorLocalTradeSelection,
  UnionStatus,
} from '../types/jobsite-visits.types';
import { getTodayDateString, isDateInFuture, dateToISOAtNoon, extractDateFromISO } from '../utils/dateUtils';
import { getJobsiteCompletionPercentage } from '../utils/jobsiteUtils';

// Helper function to format union type display
const formatUnionType = (unionType: string): string => {
  switch (unionType) {
    case 'union':
      return 'Union';
    case 'clac':
      return 'CLAC';
    case 'merit':
      return 'Merit';
    case 'other':
      return 'Unrepresented';
    default:
      return unionType;
  }
};

// Navigation Component
const Navigation = memo(({ onBack }: { onBack: () => void }) => (
  <div className="mb-6 flex items-center">
    <ArrowLeft
      className="text-gray-600 mr-2 w-5 h-5 cursor-pointer"
      onClick={onBack}
      id="back-button"
    />
    <Breadcrumb title="Create Jobsite Visit" />
  </div>
));

// Form Data Interface
interface CreateVisitFormData {
  // Visit date
  visitDate: string; // ISO string format to match database

  // Basic visit info
  status: 'active' | 'inactive' | 'completed';
  jobCompletionStatus: string;
  markAsComp: boolean;

  // Multi-contractor trades data (NEW)
  contractorTrades: {
    contractors: ContractorLocalTradeSelection[];
  };

  // Files data
  files: File[];

  // Additional visit data
  comments: string;
}

// Step configuration
const FORM_STEPS = [
  { id: 1, title: 'Jobsite Visit Date', description: 'When did you visit this jobsite?' },
  { id: 2, title: 'Trades Information', description: 'Trade details and contractor assignments' },
  { id: 3, title: 'Files & Documents', description: 'Upload supporting documents and photos' },
  { id: 4, title: 'Status & Completion', description: 'Job completion status and progress' },
  { id: 5, title: 'Review & Submit', description: 'Review all information and submit' }
];

const CreateJobsiteVisit: React.FC = () => {
  const navigate = useNavigate();
  const params = useParams();
  const jobId = params.id; // Note: route is /details/:id/create-visit so we use params.id

  // Custom hooks - using the same pattern as jobsite-details
  const {
    jobsiteData,
  } = useJobsiteData(jobId, false);

  // Use the same form hook as jobsite-details for consistency
  const {
    completionSteps,
  } = useEditForm();

  // File upload hook
  const { uploadFiles } = useJobsiteVisitFileUpload();

  // State management
  const [currentStep, setCurrentStep] = useState(1);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingText, setProcessingText] = useState('');
  const [hasEdits, setHasEdits] = useState(false);
  const [contractors, setContractors] = useState<any[]>([]); // Contractors with trades for multi-contractor step
  

  // Form data
  const [formData, setFormData] = useState<CreateVisitFormData>({
    visitDate: '', // No default date - user must select a date
    status: 'active',
    jobCompletionStatus: '',
    markAsComp: false,
    contractorTrades: {
      contractors: []
    },
    files: [],
    comments: ''
  });

  const initialFormDataRef = useRef<CreateVisitFormData | null>(null);

  // Fetch contractors with trades for multi-contractor step
  useEffect(() => {
    const fetchContractorsWithTrades = async () => {
      try {
        const contractorsData = await ContractorService.getContractorWithTrades();
        setContractors(contractorsData);
      } catch (error) {
        console.error('Error fetching contractors with trades:', error);
        toast.error('Failed to load contractors');
      }
    };

    fetchContractorsWithTrades();
  }, []);

  useEffect(() => {
  // Only set hasEdits to true if we have an initial reference and the data has actually changed
  if (initialFormDataRef.current) {
    const isEdited = JSON.stringify(formData) !== JSON.stringify(initialFormDataRef.current);
    setHasEdits(isEdited);
  }
}, [formData]);

useEffect(() => {
  const handleBeforeUnload = () => {
    if (hasEdits) {
      toast.warn('You have unsaved changes!');
    }
  };

  window.addEventListener('pagehide', handleBeforeUnload);

  return () => window.removeEventListener('pagehide', handleBeforeUnload)
}, [hasEdits]);


  // Note: Old trades state removed - now using formData.contractorTrades structure

  // Validation state
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  // Dialog states
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);

  // Get current user
  const user = authService.getUser();


  // Format address (following jobsite-details pattern)
  const formatAddress = useMemo(() => {
    if (!jobsiteData?.address?.[0]) return '';

    const address = jobsiteData.address[0];
    const parts = [
      address.address1,
      address.address2,
      address.city,
      address.postalCode
    ].filter(Boolean);

    return parts.join(', ');
  }, [jobsiteData?.address]);

  // Lookups data is now provided by useEditForm hook

  // Set default values from jobsite data when it loads
  useEffect(() => {
    const prefilledFormData = { ...formData };
    if (jobsiteData && jobsiteData.jobDetails[0] && user) {
      // Set default values from jobsite
      prefilledFormData.jobCompletionStatus = jobsiteData.jobDetails[0].jobCompletionStatus?.id || ''
    }

    setFormData(prefilledFormData);
    // Set the initial reference after prefilled data is loaded
    initialFormDataRef.current = prefilledFormData;

  }, [jobsiteData, user]);

  // Navigation handlers
  const handleBack = useCallback(() => {
    if (hasEdits) {
      toast.warn('You have made edits, and it has not been saved');
    }
    navigate(`/jobsite-management/details/${jobId}`);
  }, [hasEdits, navigate, jobId]);

  // Step validation
  const validateCurrentStep = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    switch (currentStep) {
      case 1: // Visit Date step
        if (!formData.visitDate) {
          newErrors.visitDate = 'Visit date is required';
          toast.error('Visit date is required');
        }
        break;

      case 2: // Multi-Contractor Trades step
        // Contractors are OPTIONAL, but if selected, must be complete
        formData.contractorTrades.contractors.forEach((contractor) => {
          // If contractor is selected, validate it has at least one local
          if (contractor.locals.length === 0) {
            newErrors[`contractor_${contractor.contractorId}_locals`] = `Please select at least one local for ${contractor.contractorName}`;
            toast.error(`Please select at least one local for ${contractor.contractorName}`);
          }

          // Validate each local
          contractor.locals.forEach((local) => {
            // If local is selected, validate it has at least one trade
            if (local.trades.length === 0) {
              newErrors[`contractor_${contractor.contractorId}_local_${local.localUnionId}_trades`] = `Please select at least one trade for ${local.localUnionName}`;
              toast.error(`Please select at least one trade for ${local.localUnionName} under ${contractor.contractorName}`);
            }

            // Validate each trade
            local.trades.forEach((trade) => {
              // Validate union type is selected
              if (!trade.unionType) {
                newErrors[`contractor_${contractor.contractorId}_local_${local.localUnionId}_trade_${trade.tradeId}_unionType`] = `Please select union type for ${trade.tradeName}`;
                toast.error(`Please select union type for ${trade.tradeName}`);
              }

              // Validate at least one of journeymen or apprentices is greater than 0
              if (trade.journeymen === 0 && trade.apprentices === 0) {
                newErrors[`contractor_${contractor.contractorId}_local_${local.localUnionId}_trade_${trade.tradeId}_workers`] = `Please enter at least one journeyman or apprentice for ${trade.tradeName}`;
                toast.error(`Please enter at least one journeyman or apprentice for ${trade.tradeName}`);
              }
            });
          });
        });
        break;

      case 3: // Files step - Optional but can add basic validation
        // Files are optional for visits, so no required validation
        // But we can validate file count and sizes if files are selected
        if (formData.files.length > 10) {
          newErrors.files = 'Maximum 10 files allowed';
          toast.error('Maximum 10 files allowed');
        }

        // Validate individual file sizes
        const invalidFiles = formData.files.filter(file => file.size > 50 * 1024 * 1024);
        if (invalidFiles.length > 0) {
          newErrors.files = `${invalidFiles.length} file(s) exceed 50MB limit`;
          toast.error(`${invalidFiles.length} file(s) exceed 50MB limit`);
        }
        break;

      case 4: // Status step
        if (!formData.jobCompletionStatus && !formData.markAsComp) {
          newErrors.jobCompletionStatus = 'Job completion status is required';
          toast.error('Job completion status is required');
        }
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Step navigation
  const handleNextStep = async () => {
    if (validateCurrentStep()) {
      if (currentStep < FORM_STEPS.length) {
        setCurrentStep(currentStep + 1);
      } else {
        setShowConfirmDialog(true);
      }
    }
  };

  const handlePreviousStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      setErrors({});
    }
  };

  // File handling
  const handleFileUpload = (files: FileList | File[]) => {
    const fileArray = Array.from(files);

    // Check if adding new files would exceed the limit
    const currentFileCount = formData.files.length;
    const availableSlots = 10 - currentFileCount;

    if (availableSlots <= 0) {
      toast.error('Maximum 10 files allowed. Please remove some files before adding more.');
      return;
    }

    if (fileArray.length > availableSlots) {
      toast.error(`You can only add ${availableSlots} more file(s). You currently have ${currentFileCount} file(s) uploaded.`);
      return;
    }

    // Validate file types and sizes
    const validFiles = fileArray.filter(file => {
      const validTypes = ['.pdf', '.png', '.jpeg', '.jpg', '.docx', '.xlsx', '.heif', '.bmp', '.tiff', '.tif', '.xls', '.doc'];
      const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
      const isValidType = validTypes.includes(fileExtension);
      const isValidSize = file.size <= 50 * 1024 * 1024; // 50MB

      // Check if file already exists in the list
      const isDuplicate = formData.files.some(existingFile => existingFile.name === file.name);

      if (isDuplicate) {
        toast.error(`File "${file.name}" is already selected`);
        return false;
      }
      if (!isValidType) {
        toast.error(`File ${file.name} has an invalid type`);
        return false;
      }
      if (!isValidSize) {
        toast.error(`File ${file.name} is too large (max 50MB)`);
        return false;
      }
      return true;
    });

    // Only add files if validation passed
    if (validFiles.length > 0) {
      setFormData(prev => ({
        ...prev,
        files: [...prev.files, ...validFiles]
      }));
    }
  };

  const handleFileRemove = (index: number) => {
    setFormData(prev => ({
      ...prev,
      files: prev.files.filter((_, i) => i !== index)
    }));
  };

  // Form submission
  const handleSubmit = async () => {
    if (!user || !jobId) return;

    try {
      setIsProcessing(true);
      setProcessingText('Creating jobsite visit...');

      // Prepare visit data (use contractor from existing jobsite data)
      // Use the parent jobsite's current status for the visit
      // This ensures the visit captures the jobsite's status at the time of the visit
      const currentJobsiteStatus = jobsiteData?.jobDetails[0]?.status || 'active';

      const visitData: JobsiteVisitDTO = {
        visitDate: formData.visitDate,
        status: currentJobsiteStatus as 'active' | 'inactive' | 'completed',
        jobCompletionStatus: formData.jobCompletionStatus,
        markAsComp: formData.markAsComp,
        contractor: jobsiteData?.jobDetails[0]?.contractor?.id || '',
        userId: user.id,
        jobId: jobId
      };

      // Prepare files data (matching jobsite creation pattern)
      let filesData: Omit<JobsiteVisitFilesDTO, 'jobsiteVisitId'> | undefined;
      if (formData.files.length > 0) {
        filesData = {
          userId: user.id,
          files: formData.files
        };
      }

      // Create jobsite visit (matching jobsite creation pattern)
      setProcessingText('Creating jobsite visit...');

      const visitResult = await JobsiteVisitsService.createJobsiteVisit(
        visitData
      );

      if (!visitResult || !visitResult.id) {
        initialFormDataRef.current = formData;
        setHasEdits(false);
        toast.error('Failed to create jobsite visit');
        return;
      }

      // Create trades data (optional - only if contractors selected)
      if (formData.contractorTrades.contractors.length > 0) {
        // Transform multi-contractor structure to backend format
        // Group by trade to create the jobTrade array
        const tradeMap = new Map<string, {
          tradeName: string;
          associations: Array<{
            contractorId: string;
            tradeType: string;
            noOfJourneymen: number;
            noOfApperntices: number;
            localUnionId: string;
            unionType: 'union' | 'clac' | 'merit' | 'other';
          }>;
        }>();

        // Flatten the nested structure: contractors → locals → trades
        formData.contractorTrades.contractors.forEach(contractor => {
          contractor.locals.forEach(local => {
            local.trades.forEach(trade => {
              if (!tradeMap.has(trade.tradeId)) {
                tradeMap.set(trade.tradeId, {
                  tradeName: trade.tradeName,
                  associations: []
                });
              }

              tradeMap.get(trade.tradeId)!.associations.push({
                contractorId: contractor.contractorId,
                tradeType: trade.tradeId,
                noOfJourneymen: trade.journeymen,
                noOfApperntices: trade.apprentices,
                localUnionId: local.localUnionId,
                unionType: trade.unionType
              });
            });
          });
        });

        // Convert map to jobTrade array
        const jobTrade: JobTradeVisitDTO[] = Array.from(tradeMap.entries()).map(([tradeId, data]) => {
          const totalWorkers = data.associations.reduce((sum, assoc) =>
            sum + assoc.noOfJourneymen + assoc.noOfApperntices, 0
          );

          // Calculate unionStatus based on the associations for this trade
          const unionTypes = data.associations.map(a => a.unionType);
          const hasUnion = unionTypes.includes('union');
          const hasNonUnion = unionTypes.some(t => t === 'clac' || t === 'merit' || t === 'other');

          let unionStatus: UnionStatus;
          if (hasUnion && hasNonUnion) {
            unionStatus = 'mixed'; // Mix of union and non-union
          } else if (hasUnion) {
            unionStatus = 'union'; // All union
          } else {
            // All are non-union subcategories (clac, merit, other)
            unionStatus = 'other';
          }

          return {
            tradesType: tradeId,
            unionStatus: unionStatus,
            isSignatory: false, // Not applicable for visits
            localUnionIds: [], // Locals are now stored per association
            numOfWorkers: totalWorkers,
            isUACanadaTrade: true,
            tradeContractAssociation: data.associations
          };
        });

        let tradesData: CreateJobsiteVisitsTradeDTO = {
          userId: user.id,
          noOfTrades: jobTrade.length.toString(),
          jobTrade,
          jobsiteVisitId: visitResult.id
        };

        const submitedJobsiteVisitTradesData = await JobsiteVisitsService.createJobsiteVisitTrades(tradesData);
        if (!submitedJobsiteVisitTradesData) {
          toast.error('Failed to create jobsite visit trades');
          return;
        }
      }

      // Upload files separately if any files are selected
      if (formData.files.length > 0 && filesData) {
        await uploadFiles({
          files: formData.files,
          jobsiteVisitId: visitResult.id,
          userId: user.id,
          onProgress: setProcessingText,
        });
      }

      // Create comment if exists
      if (formData.comments.trim()) {
        setProcessingText('Saving comment...');
        try {
          await JobsiteVisitsService.createJobsiteVisitComment({
            comment: formData.comments,
            userId: user.id,
            jobsiteVisitId: visitResult.id
          });
        } catch (error) {
          console.error('Error creating comment:', error);
          // Don't fail the whole submission if comment fails
          toast.warn('Visit created but comment failed to save');
        }
      }

      setProcessingText('Jobsite visit created successfully!');
      toast.success('Jobsite visit created successfully!');
      setHasEdits(false);
      setShowConfirmDialog(false);
      setShowSuccessDialog(true);

      // Navigate back after a brief delay
      setTimeout(() => {
        navigate(`/jobsite-management/details/${jobId}`);
      }, 2000);

    } catch (error: any) {
      console.error('Error creating jobsite visit:', error);
      toast.error(error.message || 'Failed to create jobsite visit');
    } finally {
      setIsProcessing(false);
      setProcessingText('');
    }
  };

  // Form data change handlers
  const handleFormDataChange = (updates: Partial<CreateVisitFormData>) => {
    setFormData(prev => ({ ...prev, ...updates }));

    // Clear related errors
    const updatedErrors = { ...errors };
    Object.keys(updates).forEach(key => {
      delete updatedErrors[key];
    });
    setErrors(updatedErrors);
  };

  // Note: Multi-contractor trades component manages its own state and calls onChange


  // Render current step content
  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1: // Visit Date Step
        return (
          <div className="space-y-6">
            <h2 className="text-lg font-medium text-gray-900">Visit Date</h2>
            <p className="text-sm text-gray-600">When did you visit this jobsite?</p>

            <div className="w-full max-w-md">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date of Visit <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-red-500 focus:border-red-500"
                value={extractDateFromISO(formData.visitDate)}
                max={getTodayDateString()}
                onChange={(e) => {
                  const selectedDate = e.target.value;

                  // Validate: don't allow future dates
                  if (isDateInFuture(selectedDate)) {
                    toast.warn('Cannot select future dates');
                    return;
                  }

                  // Store date as ISO string at noon UTC to avoid timezone issues
                  const dateValue = dateToISOAtNoon(selectedDate);
                  handleFormDataChange({
                    visitDate: dateValue
                  });
                }}
              />
              {errors.visitDate && (
                <p className="text-xs text-red-500 mt-1">{errors.visitDate}</p>
              )}
              <p className="text-xs text-gray-500 mt-1">
                Select the date you visited the jobsite. This date cannot be in the future.
              </p>
            </div>
          </div>
        );

      case 2: // Multi-Contractor Trades Step
        return (
          <div className="relative">
            {/* <h2 className="text-lg font-medium text-gray-900 mb-4">Contractors & Trades</h2> */}
            <MultiContractorTradesStep
              contractors={contractors}
              selectedContractors={formData.contractorTrades.contractors}
              onChange={(contractors) => {
                setFormData(prev => ({
                  ...prev,
                  contractorTrades: { contractors }
                }));
              }}
              onContractorUpdate={(updatedContractor) => {
                // Update contractors array with the new or edited contractor
                setContractors(prev => {
                  const index = prev.findIndex(c => c.id === updatedContractor.id);
                  if (index >= 0) {
                    // Update existing contractor
                    const newContractors = [...prev];
                    newContractors[index] = updatedContractor;
                    return newContractors;
                  } else {
                    // Add new contractor
                    return [...prev, updatedContractor];
                  }
                });
              }}
              errors={errors}
            />
          </div>
        );

      case 3: // Files Step
        return (
          <div className="space-y-6">
            <h2 className="text-lg font-medium text-gray-900">Upload Files & Documents</h2>

            {/* File Upload Area */}
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
              <div className="text-center">
                <Upload className="mx-auto h-12 w-12 text-gray-400" />
                <div className="mt-4">
                  <label htmlFor="file-upload" className="cursor-pointer">
                    <span className="mt-2 block text-sm font-medium text-gray-900">
                      Drop files here or click to upload
                    </span>
                    <input
                      id="file-upload"
                      type="file"
                      className="hidden"
                      multiple
                      accept=".pdf,.png,.jpeg,.jpg,.docx,.xlsx,.heif,.bmp,.tiff,.tif,.xls,.doc"
                      onChange={(e) => {
                        if (e.target.files) {
                          handleFileUpload(e.target.files);
                          // Reset input to allow checking for duplicates on every selection
                          e.target.value = '';
                        }
                      }}
                    />
                  </label>
                  <p className="mt-1 text-xs text-gray-500">
                    PDF, PNG, JPEG, DOCX, XLSX up to 50MB each (max 10 files)
                  </p>
                </div>
              </div>
            </div>

            {/* File List */}
            {formData.files.length > 0 && (
              <div className="space-y-2">
                <h3 className="text-sm font-medium text-gray-900">Selected Files</h3>
                {formData.files.map((file, index) => (
                  <div key={index} className="flex items-center justify-between p-3 border border-gray-200 rounded-md">
                    <div className="flex items-center">
                      <div className="w-2 h-2 bg-green-500 rounded-full mr-3"></div>
                      <span className="text-sm text-gray-900">{file.name}</span>
                      <span className="ml-2 text-xs text-gray-500">
                        ({(file.size / 1024 / 1024).toFixed(1)} MB)
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleFileRemove(index)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        );

      case 4: // Status & Completion Step
        return (
          <div className="space-y-6">
            <h2 className="text-lg font-medium text-gray-900">Job Status & Progress</h2>

            {/* Job Completion Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Job Completion Status <span className="text-red-500">*</span>
              </label>
              <SelectDropdown
                options={completionSteps}
                onSelect={(value) => handleFormDataChange({ jobCompletionStatus: value })}
                getValue={(option) => option.value}
                getDisplayText={(option) => option.label}
                selectedValue={formData.jobCompletionStatus}
                placeholder="Select completion status"
                disabled={formData.markAsComp}
                testId="completion-status-select"
              />
              {errors.jobCompletionStatus && (
                <p className="text-xs text-red-500 mt-1">{errors.jobCompletionStatus}</p>
              )}
            </div>

            {/* Progress Bar */}
            {formData.jobCompletionStatus && (
              <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[16px] font-[700] text-black">Job Completion</span>
                  <span className="text-sm sm:text-md">
                    {getJobsiteCompletionPercentage(
                      completionSteps.find(s => s.value === formData.jobCompletionStatus)?.label || ''
                    )}%
                  </span>
                </div>
                <div className="relative">
                  <div className="w-full bg-gray-200 h-2 rounded-full">
                    <div
                      className="absolute left-0 top-0 bg-[#A42823] h-2 rounded-full"
                      style={{
                        width: `${getJobsiteCompletionPercentage(
                          completionSteps.find(s => s.value === formData.jobCompletionStatus)?.label || ''
                        )}%`
                      }}
                    ></div>
                  </div>
                </div>
              </div>
            )}


            {/* Comments */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Additional Comments
              </label>
              <textarea
                value={formData.comments}
                onChange={(e) => handleFormDataChange({ comments: e.target.value })}
                rows={4}
                className="w-full p-3 border border-gray-300 rounded-md focus:ring-red-500 focus:border-red-500"
                placeholder="Add any additional notes about this visit..."
              />
            </div>
          </div>
        );

      case 5: // Review Step
        return (
          <div className="space-y-6">
            <h2 className="text-lg font-medium text-gray-900">Review & Submit</h2>

            {/* Review sections */}
            <div className="bg-gray-50 p-6 rounded-md space-y-6">
              {/* Visit Date Summary */}
              <div>
                <h3 className="font-medium text-gray-900 mb-2">Visit Date</h3>
                <p className="text-sm text-gray-600">
                  <strong>Date:</strong> {new Date(formData.visitDate).toLocaleDateString('en-US', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    timeZone: 'UTC'
                  })}
                </p>
              </div>

              {/* Contractor Trades Summary */}
              {formData.contractorTrades.contractors.length > 0 && (
                <div>
                  <h3 className="font-medium text-gray-900 mb-3">Mechanical Contractors & Trades</h3>
                  <div className="text-sm text-gray-600 space-y-4">
                    {formData.contractorTrades.contractors.map((contractor, idx) => (
                      <div key={idx} className="border-l-4 border-gray-700 pl-4">
                        <p className="font-semibold text-gray-900 mb-2">{contractor.contractorName}</p>
                        {contractor.locals.map((local, localIdx) => (
                          <div key={localIdx} className="ml-2 mb-3">
                            <p className="font-medium text-gray-800">{local.localUnionName}</p>
                            <div className="ml-4 mt-2 space-y-2">
                              {local.trades.map((trade, tradeIdx) => (
                                <div key={tradeIdx} className="bg-gray-100 p-3 rounded">
                                  <p className="font-medium text-gray-900">{trade.tradeName}</p>
                                  <div className="mt-1 grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
                                    <p><strong>Journeymen:</strong> {trade.journeymen}</p>
                                    <p><strong>Apprentices:</strong> {trade.apprentices}</p>
                                    <p><strong>Union Status:</strong> {formatUnionType(trade.unionType)}</p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Files Summary */}
              {formData.files.length > 0 && (
                <div>
                  <h3 className="font-medium text-gray-900 mb-2">Files</h3>
                  <p className="text-sm text-gray-600 mb-2">
                    <strong>Total Files:</strong> {formData.files.length}
                  </p>
                  <div className="space-y-2">
                    {formData.files.map((file, idx) => (
                      <div key={idx} className="bg-gray-100 p-2 rounded text-sm">
                        <p className="text-gray-900">{file.name}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Status Summary */}
              <div>
                <h3 className="font-medium text-gray-900 mb-2">Job Status</h3>
                <div className="text-sm text-gray-600 space-y-1">
                  <p>
                    <strong>Completion Status:</strong> {
                      completionSteps.find(s => s.value === formData.jobCompletionStatus)?.label || 'Not selected'
                    }
                  </p>
                </div>
              </div>

              {/* Comments Summary */}
              {formData.comments && (
                <div>
                  <h3 className="font-medium text-gray-900 mb-2">Comments</h3>
                  <p className="text-sm text-gray-600">{formData.comments}</p>
                </div>
              )}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // Show loading skeleton while jobsite data is loading
  if (!jobsiteData) {
    return (
      <div className="bg-gray-50 p-3 sm:p-6">
        <Navigation onBack={handleBack} />
        <JobsiteDetailsSkeleton />
      </div>
    );
  }

  return (
    <div className="bg-gray-50 p-3 sm:p-6">
      <Navigation onBack={handleBack} />

      {/* Header */}
      <div className="bg-white rounded-md shadow mb-4">
        <div className="p-4 sm:p-6">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h1 className="text-[24px] font-[700] mb-1">Create Jobsite Visit</h1>
              <p className="text-gray-600">{jobsiteData.jobDetails[0]?.jobSiteName}</p>
              {jobsiteData.jobDetails[0]?.subsite && (
                <p className="text-sm text-gray-500">{jobsiteData.jobDetails[0].subsite}</p>
              )}
            </div>
          </div>

          {/* Display jobsite information as read-only */}
          <div className="mt-4 p-4 bg-gray-50 rounded-md">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Address */}
              {formatAddress && (
                <div>
                  <p className="text-sm text-gray-700">
                    <span className="font-medium text-gray-900">Address:</span> {formatAddress}
                  </p>
                </div>
              )}

              {/* Contractor */}
              {jobsiteData?.jobDetails[0]?.contractor && (
                <div>
                  <p className="text-sm text-gray-700">
                    <span className="font-medium text-gray-900">General Contractor:</span> {jobsiteData.jobDetails[0].contractor.name}
                  </p>
                </div>
              )}

              {/* Site Contact */}
              {jobsiteData?.jobDetails[0]?.siteContact && (
                <div>
                  <p className="text-sm text-gray-700">
                    <span className="font-medium text-gray-900">Site Contact:</span> {jobsiteData.jobDetails[0].siteContact}
                  </p>
                </div>
              )}

              {/* Site Contact Email */}
              {jobsiteData?.jobDetails[0]?.siteContactEmail && (
                <div>
                  <p className="text-sm text-gray-700">
                    <span className="font-medium text-gray-900">Contact Email:</span> {jobsiteData.jobDetails[0].siteContactEmail}
                  </p>
                </div>
              )}

              {/* Sectors */}
              {jobsiteData?.jobDetails[0]?.jobDetailsSectors && jobsiteData.jobDetails[0].jobDetailsSectors.length > 0 && (
                <div>
                  <p className="text-sm text-gray-700">
                    <span className="font-medium text-gray-900">Sector{jobsiteData.jobDetails[0].jobDetailsSectors.length > 1 ? 's' : ''}:</span> {jobsiteData.jobDetails[0].jobDetailsSectors.map(jds => jds.sector?.name).filter(Boolean).join(', ')}
                  </p>
                </div>
              )}

              {/* Industry */}
              {jobsiteData?.jobDetails[0]?.industry && (
                <div>
                  <p className="text-sm text-gray-700">
                    <span className="font-medium text-gray-900">Industry:</span> {jobsiteData.jobDetails[0].industry.name}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Multi-step Form */}
      <div className="bg-white rounded-md shadow">
        <div className="p-4 sm:p-6">
          {/* Step Progress Bar */}
          <div className="mb-8">
            <StepProgressBar
              steps={FORM_STEPS.map(step => step.title)}
              currentStep={currentStep}
            />
          </div>

          {/* Form Content */}
          <div className="mb-8">
            {renderCurrentStep()}
          </div>

          {/* Navigation Buttons */}
          <div className="flex justify-between pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={handlePreviousStep}
              disabled={currentStep === 1}
              className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>

            <button
              type="button"
              onClick={handleNextStep}
              disabled={isProcessing}
              className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {currentStep === FORM_STEPS.length ? 'Submit' : 'Next'}
            </button>
          </div>
        </div>
      </div>

      {/* Confirmation Dialog */}
      <Dialog
        isOpen={showConfirmDialog}
        onClose={() => !isProcessing && setShowConfirmDialog(false)}
        width="w-full sm:w-[500px]"
        height="auto"
        disableBackdropClick={true}
      >
        <div className="text-center p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            {isProcessing ? 'Creating Jobsite Visit' : 'Confirm Jobsite Visit Creation'}
          </h3>
          {isProcessing ? (
            <div className="mb-6">
              <div className="flex items-center justify-center mb-4">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
              </div>
              <p className="text-sm text-gray-600">{processingText}</p>
              <p className="text-xs text-gray-500 mt-2">Please wait, do not close this window...</p>
            </div>
          ) : (
            <p className="text-sm text-gray-500 mb-6">
              Are you sure you want to create this jobsite visit? This action cannot be undone.
            </p>
          )}
          <div className="flex justify-center gap-3">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setShowConfirmDialog(false)}
              disabled={isProcessing}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn-primary"
              onClick={handleSubmit}
              disabled={isProcessing}
            >
              {isProcessing ? 'Processing...' : 'Create Visit'}
            </button>
          </div>
        </div>
      </Dialog>

      {/* Success Dialog */}
      <Dialog
        isOpen={showSuccessDialog}
        onClose={() => setShowSuccessDialog(false)}
        width="w-full sm:w-[400px]"
        height="auto"
      >
        <div className="text-center p-6">
          <div className="w-12 h-12 mx-auto mb-4 bg-green-100 rounded-full flex items-center justify-center">
            <Plus className="w-6 h-6 text-green-600" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Visit Created Successfully!
          </h3>
          <p className="text-sm text-gray-500">
            Redirecting to jobsite details...
          </p>
        </div>
      </Dialog>

    </div>
  );
};

export default CreateJobsiteVisit;